/**
 * importIdentity — one call that makes any record-connector import a
 * first-class citizen of the sync fabric: registers the external identity
 * (entity_external_ids) and emits the change event (change_events) that the
 * fire-and-forget import paths drop today.
 *
 * Convergence rule (set 3): every import that creates a platform entity from
 * an external record should call this with the created internal id and the
 * source record's external id. Without it, the entity cannot round-trip,
 * cannot be deep-linked back, and is invisible to Meridian/Beacon-class
 * consumers of the fabric.
 */

import { registerExternalId, logChangeEvent, type ChangeEventEntityType } from '../connectors';

export interface ImportedRecordIdentity {
  /** The platform entity id that was just created (or matched). */
  internalId: string;
  /** The external system's record id (e.g. a Notion page id). */
  externalId: string;
  /** Deep link back to the source record, when the connector provides one. */
  externalUrl?: string;
  /** Short label for the change-event payload (usually the title). */
  label?: string;
}

/**
 * Register identities + emit created-events for a batch of imported records.
 * Best-effort per record (mirrors registerExternalId's own posture): one
 * failure never aborts the batch; failures are returned for surfacing.
 */
export async function registerImportedRecords(params: {
  workspaceId: string;
  entityType: ChangeEventEntityType;
  connectorType: string;
  records: ImportedRecordIdentity[];
}): Promise<{ registered: number; failures: string[] }> {
  const { workspaceId, entityType, connectorType, records } = params;
  let registered = 0;
  const failures: string[] = [];
  for (const record of records) {
    if (!record.internalId || !record.externalId) {
      failures.push(`${record.label ?? record.externalId ?? 'record'}: missing id — not registered`);
      continue;
    }
    try {
      await registerExternalId({
        workspaceId,
        entityType,
        internalId: record.internalId,
        connectorType,
        externalId: record.externalId,
        externalUrl: record.externalUrl,
      });
      await logChangeEvent({
        workspaceId,
        entityType,
        entityId: record.internalId,
        eventType: 'created',
        newValue: {
          from: `${connectorType}_import`,
          externalId: record.externalId,
          ...(record.label ? { title: record.label } : {}),
        },
      });
      registered++;
    } catch (err) {
      failures.push(`${record.label ?? record.externalId}: ${err instanceof Error ? err.message : 'registration failed'}`);
    }
  }
  return { registered, failures };
}

/**
 * Read the connector's existing external → internal mappings for the given
 * external ids (entity_external_ids). Used by import duplicate-skip: a record
 * whose mapped item came from an earlier, un-retired import batch is skipped.
 * Best-effort: on any error it returns no mappings, so the import proceeds
 * exactly as it did before dedupe existed.
 */
export async function loadImportedRecordMappings(params: {
  workspaceId: string;
  connectorType: string;
  externalIds: string[];
}): Promise<Array<{ externalId: string; internalId: string }>> {
  const ids = Array.from(new Set(params.externalIds.filter(Boolean)));
  if (ids.length === 0) return [];
  try {
    const { getTypedSupabaseClient } = await import('../typedSupabaseClient');
    const out: Array<{ externalId: string; internalId: string }> = [];
    // Chunk to keep the `in` list well under URL limits.
    for (let i = 0; i < ids.length; i += 200) {
      const { data, error } = await getTypedSupabaseClient()
        .from('entity_external_ids')
        .select('external_id, internal_id')
        .eq('workspace_id', params.workspaceId)
        .eq('connector_type', params.connectorType)
        .eq('entity_type', 'item')
        .in('external_id', ids.slice(i, i + 200));
      if (error) return [];
      for (const row of data ?? []) out.push({ externalId: row.external_id, internalId: row.internal_id });
    }
    return out;
  } catch {
    return [];
  }
}
